import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';

import { gtag } from "../..";
import { useEffect } from "react";

const EstimationBestPractices = () => {
    useEffect(() => {
        gtag('event', 'page_view', {
            page_title: 'Estimation Best Practices',
            page_path: '/estimation-best-practices'
        });
    });

    return (
        <div className="about">
            <Typography component="h1" variant="h1">
                Agile Estimation Best Practices: Lessons from Real Teams
            </Typography>
            <Card>
                <CardContent className='summary-content'>
                    <Typography component="p" variant="p">
                        Estimation is one of the most debated practices in agile development. Some teams swear by it, others have abandoned it entirely in favor of "no estimates." The truth is that estimation, done well, isn't about predicting the future — it's about building shared understanding within the team.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        Estimate Effort, Not Duration
                    </Typography>
                    <Typography component="p" variant="p">
                        The most common mistake teams make is treating story points as hours. "This is a 5-point story, so it should take 5 hours" misses the entire point. Story points measure complexity and effort relative to other stories, not calendar time.
                    </Typography>
                    <Typography component="p" variant="p">
                        A task might be technically simple (low effort) but require coordinating with three external teams (high complexity). Another task might be algorithmically complex but contained to a single file. These are different kinds of "hard" and your estimation conversations should surface that distinction.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        Use Reference Stories
                    </Typography>
                    <Typography component="p" variant="p">
                        Every team should maintain a set of reference stories — well-known past tasks that anchor the scale. When someone asks "what does a 5 feel like?", you should be able to point to a concrete example that the whole team remembers.
                    </Typography>
                    <Typography component="p" variant="p">
                        Good reference stories span the range of your scale. Keep one at 1, 3, 5, 8, and 13. Write them down somewhere the whole team can see (a wiki page, a pinned Slack message, a whiteboard). Update them quarterly as the team's capabilities evolve.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        The Value is in the Conversation
                    </Typography>
                    <Typography component="p" variant="p">
                        The most important output of a <a href="/what-is-planning-poker">Planning Poker</a> session isn't the number — it's the discussion that happens when estimates diverge. When one developer says "2" and another says "8", that gap reveals a fundamental disagreement about scope, approach, or hidden complexity.
                    </Typography>
                    <Typography component="p" variant="p">
                        These conversations prevent the kind of surprises that derail sprints. The developer who estimated 2 might be thinking of a shortcut that won't work. The developer who estimated 8 might know about a hidden dependency. Without the estimation ritual, this knowledge stays locked in individual heads.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        Break Down Large Stories
                    </Typography>
                    <Typography component="p" variant="p">
                        If a story is estimated at 13 or higher, it almost certainly needs to be broken down. Large stories hide risk. They're harder to test, harder to review, and more likely to carry unspoken assumptions.
                    </Typography>
                    <Typography component="p" variant="p">
                        A useful heuristic: if a story can't be completed, tested, and reviewed within a single sprint, it's too big. Break it along natural seams — by user workflow step, by technical layer, or by acceptance criteria.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        Don't Re-estimate Completed Work
                    </Typography>
                    <Typography component="p" variant="p">
                        When a 3-point story takes much longer than expected, resist the temptation to go back and change it to an 8. The original estimate reflected the team's understanding at that moment. The discrepancy is useful data — it tells you something about what the team doesn't yet know about that area of the codebase.
                    </Typography>
                    <Typography component="p" variant="p">
                        Instead, track the discrepancy and discuss it in retrospectives. Over time, patterns emerge: "we consistently underestimate database migration tasks" or "stories involving the legacy payment module always take 2x longer than estimated."
                    </Typography>

                    <Typography component="h2" variant="h2">
                        Velocity is a Team Metric, Not an Individual One
                    </Typography>
                    <Typography component="p" variant="p">
                        Velocity (total story points completed per sprint) should only be used for sprint planning capacity — never for performance evaluation. The moment story points become a productivity measure, teams inflate estimates to look better, and the entire system loses its value.
                    </Typography>
                    <Typography component="p" variant="p">
                        A healthy velocity is a stable velocity. If your team consistently delivers 30-35 points per sprint, you can confidently plan future sprints around that range. Wild swings (15 one sprint, 50 the next) indicate scope problems, not productivity problems.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        When to Skip Estimation
                    </Typography>
                    <Typography component="p" variant="p">
                        Not everything needs to be estimated. Bug fixes with clear root causes, operational tasks, and well-understood recurring work can often skip the ceremony. The purpose of estimation is to surface unknowns — if there are no unknowns, a brief conversation is sufficient.
                    </Typography>
                    <Typography component="p" variant="p">
                        Some mature teams move to a "count stories" approach where they focus on breaking work into consistently small pieces rather than estimating each one. This works well when a team has strong decomposition discipline but can fall apart without it.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        Common Anti-Patterns to Avoid
                    </Typography>
                    <ul>
                        <li><Typography component="span" variant="body1"><strong>The loudest voice wins:</strong> If one person consistently anchors the discussion, enforce simultaneous reveal using a <a href="/">Scrum Poker tool</a>.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>Estimation by committee:</strong> If the whole team isn't in the room, the estimate is incomplete. Missing perspectives lead to missing complexity.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>Precision theater:</strong> Debating whether something is a 5 or an 8 for 15 minutes defeats the purpose. Use the <a href="/fibonacci-estimation">Fibonacci scale</a> to force meaningful distinctions.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>Ignoring QA and design effort:</strong> A story isn't done when the code is written. Include testing, review, and deployment complexity in the estimate.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>Estimating under pressure:</strong> "We need to fit 80 points into this sprint" leads to artificially low estimates. Estimate honestly, then prioritize ruthlessly.</Typography></li>
                    </ul>

                    <Typography component="p" variant="p">
                        Good estimation is a skill that improves with practice. Start your next session with <a href="/">Scrum Poker</a> and focus on the conversations, not the numbers.
                    </Typography>
                </CardContent>
            </Card>
        </div>
    );
}

export default EstimationBestPractices;
